import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

export const jobBoardSchema = z.enum([
  "INDEED",
  "LINKEDIN",
  "LEVER",
  "GREENHOUSE",
  "WORKABLE",
  "WUZZUF",
  "BAYT",
  "GULFTALENT",
  "JADARAT",
  "OTHER",
]);
export const jobStatusSchema = z.enum(["NEW", "INTERESTED", "SKIPPED"]);
export const jobSearchStatusSchema = z.enum(["PENDING", "IN_PROGRESS", "COMPLETED", "FAILED"]);
export const discoveryOutcomeSchema = z.enum(["SUCCESS", "PARTIAL", "FAILED", "NO_ELIGIBLE_WORK"]);
export const scraperAccountStatusSchema = z.enum(["ACTIVE", "BLOCKED", "RETIRED"]);

/**
 * Which engine produced a score.
 *
 * `deterministic` is a keyword-overlap heuristic, not a model judgement. It is
 * substituted silently whenever the AI path is unavailable — most importantly
 * once `AI_SCORING_DAILY_CAP` is exceeded. On 2026-08-20 that happened at
 * ~11:58 and ~7,400 jobs were stamped with heuristic scores that were
 * indistinguishable from model scores in the database and the UI, and which
 * never get re-scored because the row exists. Carrying the source makes the
 * substitution visible instead of silent.
 */
export const scoreSourceSchema = z.enum(["ai", "deterministic"]);
export type ScoreSource = z.infer<typeof scoreSourceSchema>;

export const scoreResultSchema = z.object({
  score: z.number().int().min(0).max(100),
  reason: z.string(),
  matchedKeywords: z.array(z.string()),
  missingKeywords: z.array(z.string()),
  // Optional so a cached payload written before this field existed still parses.
  source: scoreSourceSchema.optional(),
  /**
   * True when the AI path was INTENDED but unavailable — a Groq failure or the
   * daily budget being spent — so this value is a stopgap, not a verdict.
   *
   * Distinct from `source`, which says what produced the number. A FREE-tier
   * user legitimately gets `source: "deterministic"` every time and that is the
   * product working; a paid user during a 60-second cooldown gets the same
   * shape for a completely different reason. Conflating them is what let a
   * transient outage permanently cost a job its AI score: the fallback was
   * persisted, and "a JobScore row exists" is what makes a job ineligible for
   * re-scoring forever.
   *
   * Degraded results must not be persisted and must not be cached.
   */
  degraded: z.boolean().optional(),
});

export type ScoreResult = z.infer<typeof scoreResultSchema>;

export const updateJobStatusSchema = z.object({
  status: z.enum(["INTERESTED", "SKIPPED"]),
});

export class UpdateJobStatusDto extends createZodDto(updateJobStatusSchema) {}

export const createScraperAccountSchema = z.object({
  platform: jobBoardSchema,
  username: z.string().trim().min(1),
  password: z.string().min(1),
});

export class CreateScraperAccountDto extends createZodDto(createScraperAccountSchema) {}

export const updateScraperAccountStatusSchema = z.object({
  status: scraperAccountStatusSchema,
});

export class UpdateScraperAccountStatusDto extends createZodDto(updateScraperAccountStatusSchema) {}

export const jobSchema = z.object({
  id: z.string(),
  board: jobBoardSchema,
  title: z.string(),
  company: z.string(),
  location: z.string().nullable().default(null),
  salary: z.string().nullable().default(null),
  description: z.string().nullable().default(null),
  url: z.string().url(),
  postedAt: z.coerce.date().nullable().default(null),
  status: jobStatusSchema,
  isNew: z.boolean(),
  aiScore: z.number().int().nullable().default(null),
  aiScoreReason: z.string().nullable().default(null),
  matchedKeywords: z.array(z.string()),
  missingKeywords: z.array(z.string()),
  discoveredAt: z.coerce.date(),
});

export type JobDto = z.infer<typeof jobSchema>;

export const jobSearchSchema = z.object({
  id: z.string(),
  jobProfileId: z.string(),
  status: jobSearchStatusSchema,
  resultsFound: z.number().int(),
  jobsScored: z.number().int(),
  linkedinUnavailable: z.boolean().default(false),
  startedAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable().default(null),
  errorMessage: z.string().nullable().default(null),
  accountingVersion: z.number().int().optional(),
  outcome: discoveryOutcomeSchema.nullable().optional(),
  discoveredCount: z.number().int().optional(),
  matchedPoolCount: z.number().int().optional(),
});

export type JobSearchDto = z.infer<typeof jobSearchSchema>;

export const startJobSearchResponseSchema = z.object({
  jobSearchId: z.string(),
  status: jobSearchStatusSchema,
  message: z.string(),
});

export type StartJobSearchResponseDto = z.infer<typeof startJobSearchResponseSchema>;

export const paginatedJobsSchema = z.object({
  data: z.array(jobSchema),
  meta: z.object({
    total: z.number().int(),
    scored: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    totalPages: z.number().int(),
  }),
});

export type PaginatedJobsDto = z.infer<typeof paginatedJobsSchema>;

export const scraperAccountSchema = z.object({
  id: z.string(),
  platform: jobBoardSchema,
  username: z.string(),
  status: scraperAccountStatusSchema,
  blockedAt: z.coerce.date().nullable().default(null),
  totalSessionsBeforeBlock: z.number().int(),
  sessionDurationMs: z.bigint().or(z.number().int()),
  lastUsedAt: z.coerce.date().nullable().default(null),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type ScraperAccountDto = z.infer<typeof scraperAccountSchema>;
